import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import DeadlineReminderModal from './components/DeadlineReminderModal';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';
import CookieBanner from './components/CookieBanner';
import InstallPrompt from './components/InstallPrompt';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Voorspel from './pages/Voorspel';
import Voorspellingen from './pages/Voorspellingen';
import Stand from './pages/Stand';
import Kalender from './pages/Kalender';
import Prijzen from './pages/Prijzen';
import Speluitleg from './pages/Speluitleg';
import OverOns from './pages/OverOns';
import Privacybeleid from './pages/Privacybeleid';
import Profiel from './pages/Profiel';
import Admin from './pages/Admin';
import { Toaster } from './components/ui/toaster';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Refresh user data from backend on app load
function UserDataRefresher() {
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      import('./services/api').then(({ refreshUserData }) => {
        refreshUserData();
      });
    }
  }, []);
  return null;
}

// Check if user must change password
function PasswordChangeChecker() {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkPasswordChange = () => {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (user && user.must_change_password) {
        setShowModal(true);
      }
    };
    
    checkPasswordChange();
    
    // Also check when storage changes (login)
    window.addEventListener('storage', checkPasswordChange);
    
    // Check periodically (for same-tab login)
    const interval = setInterval(checkPasswordChange, 1000);
    
    return () => {
      window.removeEventListener('storage', checkPasswordChange);
      clearInterval(interval);
    };
  }, []);
  
  const handleSuccess = () => {
    setShowModal(false);
  };
  
  const handleLogout = () => {
    setShowModal(false);
    navigate('/login');
  };
  
  return (
    <ForcePasswordChangeModal 
      open={showModal} 
      onSuccess={handleSuccess}
      onLogout={handleLogout}
    />
  );
}

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (Google Auth callback)
  // This must be checked synchronously during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <UserDataRefresher />
      <Navbar />
      <DeadlineReminderModal />
      <PasswordChangeChecker />
      <CookieBanner />
      <InstallPrompt />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/voorspel" element={<Voorspel />} />
        <Route path="/voorspellingen" element={<Voorspellingen />} />
        <Route path="/stand" element={<Stand />} />
        <Route path="/kalender" element={<Kalender />} />
        <Route path="/prijzen" element={<Prijzen />} />
        <Route path="/speluitleg" element={<Speluitleg />} />
        <Route path="/over-ons" element={<OverOns />} />
        <Route path="/privacybeleid" element={<Privacybeleid />} />
        <Route path="/profiel" element={<Profiel />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Footer />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background text-foreground">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
